import { TextField as MuiTextField, TextFieldProps as MuiTextFieldProps } from '@mui/material'
import { FieldValues, useController, UseControllerProps } from 'react-hook-form'
import { NumericFormat, type NumericFormatProps } from 'react-number-format'

type Props<FormValues extends FieldValues = FieldValues> = NumericFormatProps &
    MuiTextFieldProps &
    UseControllerProps<FormValues> & {
        valueAsNumber?: boolean
        min?: number
        max?: number
    }

export const NumberField = <FormValues extends FieldValues = FieldValues>({
    name,
    control,
    defaultValue,
    rules,
    shouldUnregister,
    prefix,
    suffix,
    min,
    max,
    ...restInputProps
}: Props<FormValues>): JSX.Element => {
    const {
        field: { ref: fieldRef, ...rhfFieldProps },
        fieldState: {
            // invalid,
            // isTouched,
            // isDirty,
            error,
        },
    } = useController<FormValues>({
        name,
        control, // given if nested in `FormProvider`
        defaultValue,
        rules,
        shouldUnregister,
    })

    const errorMessage = error ? error.message || '' : undefined

    const onChange: NumericFormatProps['onChange'] = (e) => {
        const removedCommas = e.target.value.replaceAll(',', '')
        if (restInputProps.onChange) {
            e.target.value = removedCommas
            restInputProps.onChange(e)
        }

        if (!restInputProps.valueAsNumber) {
            return rhfFieldProps.onChange(removedCommas)
        }

        const asNumber = +removedCommas.replaceAll(prefix || '', '').replaceAll(suffix || '', '')
        rhfFieldProps.onChange(asNumber)
    }

    const isAllowed: NumericFormatProps['isAllowed'] =
        min !== undefined || max !== undefined
            ? (values) => {
                  const { floatValue } = values
                  if (floatValue === undefined) {
                      return true
                  }

                  if (min !== undefined && min > floatValue) {
                      return false
                  }

                  if (max !== undefined && max < floatValue) {
                      return false
                  }

                  return true
              }
            : undefined

    return (
        <NumericFormat
            // @ts-expect-error TODO: Fix types
            customInput={MuiTextField}
            thousandSeparator
            inputRef={fieldRef}
            isAllowed={isAllowed}
            {...restInputProps}
            {...rhfFieldProps}
            prefix={prefix}
            suffix={suffix}
            onChange={onChange}
            onBlur={(e) => {
                rhfFieldProps.onBlur()
                restInputProps.onBlur?.(e)
            }}
            value={restInputProps.value || rhfFieldProps.value || ''}
            required={restInputProps.required || !!rules?.required}
            error={restInputProps.error || !!error}
            helperText={restInputProps.helperText || errorMessage}
            inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9,.]*',
                ...restInputProps.inputProps,
            }}
        />
    )
}

export type { Props as NumberFieldProps }
